export interface HelpEntry {
  title: string;
  body: string;
  tips?: string[];
}

// Matching logic: longest matching path wins. Exact match preferred.
export const PAGE_HELP: Record<string, HelpEntry> = {
  "/": {
    title: "Dashboard",
    body: "Your home base. Clock in and out from here, see how many hours you've put in today and this week, and view recent shifts. Admins also see a 'Needs your attention' card with anything urgent.",
    tips: ["Clock-in is desktop or kiosk only — mobile won't let you, to keep hours accurate.", "Feeling sick? Use the orange 'Call out sick' card to submit a same-day time off request and notify admin."],
  },
  "/mission": {
    title: "Where we're going",
    body: "Our mission, vision, values, and the company goals we're working toward this quarter. Your individual goals show up here too. Click 'Check in' on any goal to update progress and add notes.",
    tips: ["Goals roll up — your work ladders to your department's, which ladders to the company's."],
  },
  "/announcements": {
    title: "Announcements",
    body: "Company-wide updates from leadership. Read the pinned ones first. The daily morning standup (auto-posted at 7am) lives here too.",
  },
  "/time": {
    title: "Time Tracking",
    body: "Your time log history. See every shift you've clocked, total hours per week, and any pending verifications. To clock in or out, use the Dashboard or the kiosk.",
    tips: ["If a shift looks wrong, talk to your manager — don't edit it yourself."],
  },
  "/pto": {
    title: "Time Off",
    body: "Request time off here. Your manager gets a notification and approves or denies — you'll get an email either way. Time off is currently unpaid.",
    tips: ["For routine time off, give at least one week's notice.", "Sick today? Use the 'Call out sick' button on your dashboard instead."],
  },
  "/tasks": {
    title: "Boards",
    body: "Trello-style boards for organizing work. Drag cards between columns (Backlog, In Progress, Review, Done). Create personal boards or use team boards (Content, C-Suite, Ecom Production).",
  },
  "/goals": {
    title: "My Goals",
    body: "Personal goals your manager set for you. Mark progress as you go — this feeds into your performance review.",
  },
  "/reviews": {
    title: "My Performance Reviews",
    body: "Reviews your manager has written for you. Once a review is sent to you, you can read it and sign off.",
  },
  "/production": {
    title: "My Production Work",
    body: "Production tasks your manager assigned you — prep work, build work, packaging, QC. Hit Start when you begin, type in how many you finished and hit Complete. Block it if something's in your way.",
    tips: ["Your completed units feed the daily digest so leadership sees what got done."],
  },
  "/checklists": {
    title: "Checklists",
    body: "Recurring checklists for your role (opening shift, end of day, weekly maintenance). Check items off as you complete them.",
  },
  "/training": {
    title: "Training",
    body: "Training modules assigned to you. Complete each one — your progress is tracked.",
  },
  "/orders": {
    title: "Order Requests",
    body: "Need something for the warehouse, office, or your job? Submit a purchase request here and admin will approve and order.",
  },
  "/support": {
    title: "Customer Support",
    body: "Customer questions, complaints, and order issues. Tickets come from email (support@scentafilter.com), Shopify webhooks, and direct entry. Reply to customers right from inside HQ — no switching to Gmail.",
    tips: ["Use 'Internal note' for team-only context.", "Use 'Reply to customer' to send an email — it logs as a public comment and moves status to 'waiting customer'."],
  },
  "/one-on-ones": {
    title: "1:1 Notes",
    body: "Notes from your one-on-one conversations with your manager or your reports. Keep a running log so nothing falls through the cracks.",
  },
  "/kudos": {
    title: "Kudos",
    body: "Recognize a teammate for great work. Anyone can give kudos to anyone. It's a culture thing — use it.",
  },
  "/suggestions": {
    title: "Suggestion Box",
    body: "Got an idea to make Scenta better? Drop it here. You can submit anonymously if you want. Admin reviews everything.",
  },
  "/dev-requests": {
    title: "Core Dev Requests",
    body: "Ask for functionality our software should have but doesn't — HQ, Sunday State Hub, the kiosk, MRP, or a storefront. Requests carry your name so we can ask follow-ups, and you'll see the status here as it moves: considering, planned, in progress, shipped — or declined, always with a reason.",
  },
  "/polls": {
    title: "Polls",
    body: "Vote on team questions. Your name is never recorded — votes are fully anonymous. Admin can see results, not who voted what.",
  },
  "/wins": {
    title: "Daily Wins",
    body: "Post one thing you're proud of from today — work, personal, customer interaction, anything. React to teammates' wins. Small culture compound interest.",
  },
  "/jobs": {
    title: "Job Board",
    body: "Open roles at Scenta. Know someone great? Refer them — if they get hired, you may earn a referral bonus.",
  },
  "/directory": {
    title: "Directory",
    body: "Everyone on the team. Find contact info, job title, and who's on which department.",
  },
  "/sops": {
    title: "SOPs",
    body: "Standard Operating Procedures — how we do things. Read the SOPs for your role before you start a new task.",
  },
  "/handbook": {
    title: "Employee Handbook",
    body: "The company handbook. You signed off on it when you joined — re-read any section here any time. If we update it, you'll be asked to re-sign.",
  },
  "/documents": {
    title: "Documents",
    body: "Catalogs, brand books and the Google Drive folders you need, plus links to the tools you use. Filtered to what your role can see.",
  },
  "/chat": {
    title: "Chat",
    body: "Quick team messages. Direct message anyone or jump into a channel. Use @ to mention someone — they'll get a notification. Click 'Start huddle' to launch a Google Meet right from here.",
    tips: ["Keep work chat in here, not in personal messengers — easier to find things later.", "Type @ to autocomplete a name."],
  },
  "/huddle": {
    title: "Video Huddle",
    body: "Drop into a Google Meet room for a quick call. Each room is persistent — click Join and you're in. Free, fast, no scheduling needed.",
    tips: ["You'll sign in with your Google / @scentafilter.com account."],
  },
  "/profile": {
    title: "My Profile",
    body: "Your personal info, emergency contacts, documents, banking details for payroll. Also where you change your password, turn on 2FA, switch language (English / Español), and pick dark mode.",
  },

  // ===== ADMIN =====
  "/admin/employees": {
    title: "Employees",
    body: "The master roster. Invite new hires, edit profiles, terminate, and manage role + permissions. Kiosk PINs are set here too.",
    tips: ["Click 'Invite employee' to send a welcome email so they can set their password and finish onboarding.", "Use 'Kiosk PINs' for bulk setup."],
  },
  "/admin/access": {
    title: "Access Review",
    body: "Pick any employee to see exactly what they have access to — pages, SOPs, resources, Drive folders. Plus compliance flags: handbook signed, I-9, W-4, background check, expiring documents. Use this to audit before launch.",
  },
  "/admin/production": {
    title: "Production Board",
    body: "Assign production work to warehouse staff — prep, build, packaging, QC, fulfillment. Set qty assigned and unit. Completed work feeds the daily digest and productivity dashboard.",
    tips: ["Link to a finished good from the catalog OR type a free-form product label."],
  },
  "/admin/productivity": {
    title: "Productivity Dashboard",
    body: "Per-employee output over 7/30/90 days. Combines clocked hours + HQ production tasks + MRP production runs. Surfaces top producer and most efficient (units/hour). Real data for performance reviews.",
  },
  "/admin/time-payroll": {
    title: "Time Tracking",
    body: "Every clocked shift across the team. Filter, edit, and export to CSV for QuickBooks. Manage time policy (max daily hours, auto-close threshold, alert recipients) here too.",
    tips: ["Payroll is processed in QuickBooks — this is for tracking only."],
  },
  "/admin/pto": {
    title: "Time Off Requests",
    body: "Approve or deny employee time off. Click Approve / Deny — the employee gets an email with the decision.",
  },
  "/admin/reviews": {
    title: "Performance Reviews",
    body: "Create and manage employee performance reviews. Set a review cycle (quarterly / semi-annual / annual) per employee. Reviews due soon show on your dashboard.",
  },
  "/admin/orders": {
    title: "Order Queue",
    body: "Employee purchase requests. Approve, deny, mark as ordered, or close.",
  },
  "/admin/vendors": {
    title: "Vendors",
    body: "Master list of suppliers and vendors. Contact info, terms, payment details. Link from purchase orders here.",
  },
  "/admin/training": {
    title: "Training Modules",
    body: "Training content you assign to employees — onboarding, safety, role-specific. Track who's completed what.",
  },
  "/admin/checklists": {
    title: "Checklists Admin",
    body: "Build recurring checklists for your team — opening shift, weekly maintenance, monthly inventory. Assign by role or by employee.",
  },
  "/admin/dev-requests": {
    title: "Core Dev Requests",
    body: "Requests for new functionality across the application suite, oldest waiting first. Every decision notifies the requester; declining requires a reason.",
  },
  "/admin/suggestions": {
    title: "Suggestions Inbox",
    body: "Employee suggestions, including anonymous ones. Read, respond, or convert into a Roadmap item.",
  },
  "/admin/offboarding": {
    title: "Offboarding",
    body: "Offboarding checklists for departing employees — return company property, revoke access, final paperwork, exit interview notes.",
  },
  "/admin/discipline": {
    title: "Discipline",
    body: "Document corrective action and disciplinary write-ups. Progressive discipline log: verbal → written → final → termination. Required for legal protection.",
  },
  "/admin/safety": {
    title: "Safety",
    body: "Log workplace injuries, near-misses, and safety incidents. Required for workers' comp and OSHA documentation.",
  },
  "/admin/background-checks": {
    title: "Background Checks",
    body: "Track background check status per employee — provider, completion date, expiration. Expiring ones show on your dashboard.",
  },
  "/admin/sops": {
    title: "Manage SOPs",
    body: "Create and edit Standard Operating Procedures. Set visibility per role so warehouse only sees production SOPs, etc.",
  },
  "/admin/documents": {
    title: "Manage Documents",
    body: "Curated document and link list shown to employees on the Documents page. Set visibility per role.",
  },
  "/admin/drive": {
    title: "Google Drive",
    body: "OAuth-connected Drive integration. Set which roles can see which top-level folders. 'Apply defaults' auto-assigns roles based on folder name (HR/payroll → admin only, accounting → admin + accountant, everything else → all).",
  },
  "/admin/chat": {
    title: "Chat Management",
    body: "Create and archive chat channels. Set channel descriptions and access.",
  },
  "/admin/audit": {
    title: "Audit Log",
    body: "Every important action in HQ — who did what, when. Compliance and forensics.",
  },
  "/admin/roadmap": {
    title: "Software Roadmap",
    body: "Features queued, in-progress, shipped, or dropped for Sunday State HQ itself. Add new ideas, ship completed work, prioritize. This is your internal product backlog.",
  },
  "/admin/bugs": {
    title: "Bug Reports",
    body: "Bugs employees filed via the red Report-a-bug button. Triage, mark fixed, or promote to the Roadmap to track the fix.",
  },
  "/admin/objectives": {
    title: "Mission & Objectives",
    body: "Edit the company mission, vision, and core values. Manage the goal tree — company → department → team → individual. Set pinned goals that show first on the Mission page.",
  },
  "/admin/decisions": {
    title: "Decision Log",
    body: "Record key business decisions — what you decided, why, what alternatives you considered, and what you expected. Set a review date to come back and capture the actual outcome. Cultural transparency and post-mortem ready.",
  },
  "/admin/jobs": {
    title: "Functions & Hiring",
    body: "Three things in one: (1) Functions Map — every business function with current owner and VA-suitability flag, surfaces high-leverage hires; (2) Open Positions — job postings; (3) Referrals — employee-referred candidates.",
    tips: ["The 'X high-leverage VA hires' banner counts functions tagged va_great that are currently in gap or underwater status — your biggest hiring wins."],
  },
  "/admin/inventory-forecast": {
    title: "Inventory Forecast",
    body: "What's running out, what's healthy. Days of supply per SKU based on the last 30 days of outflow. Grouped by health (stockout, critical, reorder, low, healthy). Export reorder list as CSV to send to your bookkeeper or vendor.",
    tips: ["By default we hide SKUs with no stock + no sales in 30 days — likely discontinued. Toggle 'Show inactive SKUs' to see them."],
  },
  "/admin/payroll-input": {
    title: "Payroll Input",
    body: "Legacy page from when payroll was in HQ. Payroll is now processed in QuickBooks — this page may be removed.",
  },
};

export function findHelp(pathname: string): HelpEntry | null {
  // Exact match first
  if (PAGE_HELP[pathname]) return PAGE_HELP[pathname];
  // Strip query strings and trailing slashes
  const clean = pathname.replace(/\/$/, "").split("?")[0];
  if (PAGE_HELP[clean]) return PAGE_HELP[clean];
  // Longest-prefix match for nested routes (e.g., /tasks/:boardId → /tasks)
  const candidates = Object.keys(PAGE_HELP)
    .filter(k => clean.startsWith(k + "/") || clean === k)
    .sort((a, b) => b.length - a.length);
  if (candidates[0]) return PAGE_HELP[candidates[0]];
  return null;
}
